import { useCallback, useEffect, useRef, useState } from "react";
import Uppy, { type Meta, type UploadResult, type UppyFile } from "@uppy/core";
import AwsS3, { type AwsS3UploadParameters } from "@uppy/aws-s3";
import { useQueryClient } from "@tanstack/react-query";

import {
  getGetAllTasksQueryKey,
  markTasksAsCompleted,
  markTasksAsFailed,
} from "api/tasks/tasks";
import { TaskStatus, type TaskResponse } from "api/model";
import { createImageUploads } from "api/uploads/uploads";
import {
  createUploadingOrtho,
  getGetOrthosQueryKey,
  startOrthoCreation,
} from "api/orthos/orthos";

import type { OrthoUploadSession } from "./orthoUpload";
import {
  getFileDirectoryPath,
  getTaskFlightDate,
  getTaskOrthoId,
  getTaskTopLevelFolderPath,
  hashFilesSha256,
  isZipFile,
} from "./orthoUpload";
import {
  AUTO_RETRY_DELAYS_MS,
  isRetryableUploadError,
  putBlobWithRetry,
  runWithConcurrency,
  UPLOAD_CONCURRENCY,
  wait,
} from "./orthoUploadTransfer";
import {
  hashOrthoZipEntries,
  openOrthoZip,
  type OrthoZipArchive,
  type OrthoZipImageEntry,
} from "./orthoZip";
import { formatFlightDateTime } from "./orthoWorkflow";

interface OrthoFileMeta extends Meta {
  uploadUrl?: string;
}

type OrthoUppyFile = UppyFile<OrthoFileMeta, Record<string, never>>;
type OrthoUppy = Uppy<OrthoFileMeta, Record<string, never>>;

/** Per-file upload state, filled in as `upload`/`resumeUploads` learn more about it. */
interface PreparedUpload {
  file: File;
  directoryPath: string;
  fileHash: string;
  taskId?: number;
  uploadUrl?: string;
  uppyFileId?: string;
}

/** A zip entry to upload; unlike `PreparedUpload` it is only decompressed when its turn comes. */
interface PreparedZipUpload {
  entry: OrthoZipImageEntry;
  taskId: number;
  uploadUrl: string;
}

/** A file that still failed after retries. */
interface FailedUpload {
  name: string;
  error?: string | null;
}

/** Builds the AwsS3 upload request from the presigned URL stashed in file meta. */
const getUploadParameters = (file: OrthoUppyFile): AwsS3UploadParameters => {
  if (typeof file.meta.uploadUrl !== "string") {
    throw new Error(`Missing upload URL for ${file.name}`);
  }
  return { method: "PUT", url: file.meta.uploadUrl };
};

/** Reads the upload URL off the task's "initial" progress entry. */
const extractUploadUrl = (task: TaskResponse): string | undefined => {
  const initialEntry = task.results?.progress?.find(
    (entry) => entry.message === "initial",
  );
  if (initialEntry?.data == null || Array.isArray(initialEntry.data)) {
    return undefined;
  }
  const { uploadUrl } = initialEntry.data;
  return typeof uploadUrl === "string" ? uploadUrl : undefined;
};

/** Pairs each zip entry with its hash and the presigned URL of the task registered for that hash. */
const toPreparedZipUploads = (
  entries: OrthoZipImageEntry[],
  fileHashes: string[],
  taskByHash: Map<string, TaskResponse>,
): PreparedZipUpload[] =>
  entries.flatMap((entry, index) => {
    const task = taskByHash.get(fileHashes[index]);
    const uploadUrl = task != null ? extractUploadUrl(task) : undefined;
    return task != null && uploadUrl != null
      ? [{ entry, taskId: task.id, uploadUrl }]
      : [];
  });

/** Tasks keyed by file hash; tasks without one can't be matched to a file. */
const mapTasksByHash = (tasks: TaskResponse[]): Map<string, TaskResponse> =>
  new Map(
    tasks.flatMap((task) =>
      task.file_hash != null ? [[task.file_hash, task] as const] : [],
    ),
  );

/**
 * Creates an Uppy instance configured for single-PUT S3 uploads using presigned URLs
 * set via file meta (see `getUploadParameters`).
 */
const createOrthoUploader = (): OrthoUppy =>
  new Uppy<OrthoFileMeta, Record<string, never>>({
    autoProceed: false,
  }).use(AwsS3<OrthoFileMeta, Record<string, never>>, {
    shouldUseMultipart: false,
    getUploadParameters,
  });

const TASK_OUTCOME_FLUSH_INTERVAL_MS = 10_000;

/** Marks the given task ids completed/failed on the backend. */
const markTaskOutcomes = async (
  completedTaskIds: number[],
  failedTaskIds: number[],
): Promise<void> => {
  await Promise.all([
    completedTaskIds.length > 0
      ? markTasksAsCompleted({
          tasks: completedTaskIds.map((taskId) => ({ task_id: taskId })),
        })
      : undefined,
    failedTaskIds.length > 0
      ? markTasksAsFailed({
          tasks: failedTaskIds.map((taskId) => ({ task_id: taskId })),
        })
      : undefined,
  ]);
};

/** Clears all files currently tracked by the Uppy instance and re-adds the given files. */
const replaceUppyFiles = (uppy: OrthoUppy, files: File[]): void => {
  uppy.getFiles().forEach((existingFile) => {
    uppy.removeFile(existingFile.id);
  });
  files.forEach((file) => {
    uppy.addFile({
      name: file.name,
      type: file.type,
      data: file,
    });
  });
};

/**
 * Drives ortho image uploads for one session at a time via a single shared Uppy
 * instance: `addFiles` stages files without uploading; `upload` registers and uploads
 * new files; `resumeUploads` finishes files that already have a task but never
 * completed. Progress/status/error state is reported back into the session identified
 * by `sessionId` via `patchSession`, rather than being owned here. A failure to
 * start ortho creation after the upload is reported via `onOrthoCreationError`.
 */
export const useOrthoUploader = (
  patchSession: (sessionId: string, patch: Partial<OrthoUploadSession>) => void,
  onOrthoCreationError: (orthoId: number) => void,
): {
  uppy: OrthoUppy;
  addFiles: (sessionId: string, files: File[]) => void;
  upload: (
    sessionId: string,
    files: File[],
    locationUuid: string,
    flightDateTime: Date,
    siteId: number,
    existingOrthoId?: number | null,
  ) => Promise<void>;
  resumeUploads: (
    sessionId: string,
    files: File[],
    tasks: TaskResponse[],
  ) => Promise<void>;
} => {
  const [uppy] = useState<OrthoUppy>(createOrthoUploader);
  const queryClient = useQueryClient();

  const totalFileCountRef = useRef(0);
  const completedFileCountRef = useRef(0);
  const uppyFileTaskIdsRef = useRef<Map<string, number>>(new Map());
  const pendingCompletedTaskIdsRef = useRef<Set<number>>(new Set());
  const pendingFailedTaskIdsRef = useRef<Set<number>>(new Set());
  /** Automatic retries already scheduled per Uppy file id. */
  const retryAttemptsRef = useRef<Map<string, number>>(new Map());
  /** Files whose latest error will be retried, so it must not be counted as a final failure yet. */
  const retryingFileIdsRef = useRef<Set<string>>(new Set());
  /** Session id the Uppy event listeners below should report progress into. */
  const activeSessionIdRef = useRef<string | null>(null);

  /** Pushes the ref-tracked counters (source of truth) into the active session so the UI re-renders. */
  const syncFileCounts = useCallback(() => {
    if (activeSessionIdRef.current == null) {
      return;
    }
    patchSession(activeSessionIdRef.current, {
      uploadedFileCount: completedFileCountRef.current,
      totalFileCount: totalFileCountRef.current,
    });
  }, [patchSession]);

  /** Sends any buffered per-file outcomes to the backend and clears the buffers. */
  const flushPendingTaskOutcomes = useCallback(async () => {
    const completedTaskIds = Array.from(pendingCompletedTaskIdsRef.current);
    const failedTaskIds = Array.from(pendingFailedTaskIdsRef.current);
    if (completedTaskIds.length === 0 && failedTaskIds.length === 0) {
      return;
    }
    pendingCompletedTaskIdsRef.current.clear();
    pendingFailedTaskIdsRef.current.clear();
    await markTaskOutcomes(completedTaskIds, failedTaskIds);
  }, []);

  /** Counts a file as finished and buffers its task outcome for the next flush. */
  const recordFileOutcome = useCallback(
    (taskId: number | undefined, outcome: "completed" | "failed") => {
      completedFileCountRef.current += 1;
      syncFileCounts();
      if (taskId != null) {
        (outcome === "completed"
          ? pendingCompletedTaskIdsRef
          : pendingFailedTaskIdsRef
        ).current.add(taskId);
      }
    },
    [syncFileCounts],
  );

  useEffect(() => {
    const onFileSuccess = (file: OrthoUppyFile | undefined) => {
      recordFileOutcome(
        file != null ? uppyFileTaskIdsRef.current.get(file.id) : undefined,
        "completed",
      );
    };
    const onFileError = (file: OrthoUppyFile | undefined, error: unknown) => {
      if (file != null) {
        const attempts = retryAttemptsRef.current.get(file.id) ?? 0;
        if (
          attempts < AUTO_RETRY_DELAYS_MS.length &&
          isRetryableUploadError(error)
        ) {
          // runUppyUpload retries it once this upload round settles.
          retryAttemptsRef.current.set(file.id, attempts + 1);
          retryingFileIdsRef.current.add(file.id);
          return;
        }
      }
      recordFileOutcome(
        file != null ? uppyFileTaskIdsRef.current.get(file.id) : undefined,
        "failed",
      );
    };

    uppy.on("upload-success", onFileSuccess);
    uppy.on("upload-error", onFileError);

    return () => {
      uppy.off("upload-success", onFileSuccess);
      uppy.off("upload-error", onFileError);
    };
  }, [uppy, recordFileOutcome]);

  /**
   * Retries files whose errors `onFileError` flagged as retryable, in rounds with
   * backoff, until they succeed or run out of retries. Returns a result whose `failed`
   * holds only the files that are still failing.
   */
  const retryFailedUploads = useCallback(
    async (
      initialResult:
        UploadResult<OrthoFileMeta, Record<string, never>> | undefined,
    ): Promise<
      UploadResult<OrthoFileMeta, Record<string, never>> | undefined
    > => {
      if (initialResult == null) {
        return undefined;
      }
      const failedById = new Map(
        initialResult.failed?.map((file) => [file.id, file]),
      );
      const successful = [...(initialResult.successful ?? [])];

      for (;;) {
        const retryIds = [...failedById.keys()].filter((id) =>
          retryingFileIdsRef.current.has(id),
        );
        retryingFileIdsRef.current.clear();
        if (retryIds.length === 0) {
          break;
        }

        const attempt = Math.max(
          ...retryIds.map((id) => retryAttemptsRef.current.get(id) ?? 1),
        );
        await wait(AUTO_RETRY_DELAYS_MS[attempt - 1]);

        const roundResults = await Promise.all(
          retryIds.map((id) => uppy.retryUpload(id)),
        );
        for (const roundResult of roundResults) {
          for (const file of roundResult?.successful ?? []) {
            failedById.delete(file.id);
            successful.push(file);
          }
          for (const file of roundResult?.failed ?? []) {
            failedById.set(file.id, file);
          }
        }
      }

      return {
        ...initialResult,
        successful,
        failed: [...failedById.values()],
      };
    },
    [uppy],
  );

  /** Resets per-transfer counters and buffers, and moves the session to "uploading". */
  const beginTransfer = useCallback(
    (sessionId: string, uploadCount: number, initialCompletedCount: number) => {
      activeSessionIdRef.current = sessionId;
      uppyFileTaskIdsRef.current.clear();
      pendingCompletedTaskIdsRef.current.clear();
      pendingFailedTaskIdsRef.current.clear();
      retryAttemptsRef.current.clear();
      retryingFileIdsRef.current.clear();
      totalFileCountRef.current = uploadCount + initialCompletedCount;
      completedFileCountRef.current = initialCompletedCount;
      syncFileCounts();
      patchSession(sessionId, { status: "uploading" });
    },
    [patchSession, syncFileCounts],
  );

  /** Runs `transfer`, flushing buffered task outcomes to the backend periodically and once more at the end. */
  const withTaskOutcomeFlushing = useCallback(
    async <T>(transfer: () => Promise<T>): Promise<T> => {
      const flushIntervalId = setInterval(() => {
        void flushPendingTaskOutcomes();
      }, TASK_OUTCOME_FLUSH_INTERVAL_MS);
      try {
        return await transfer();
      } finally {
        clearInterval(flushIntervalId);
        // Catch any outcomes that landed after the last interval tick.
        await flushPendingTaskOutcomes();
      }
    },
    [flushPendingTaskOutcomes],
  );

  /**
   * Wraps up a transfer: starts stitching for the ortho placeholder created before
   * upload (unless several files failed) and settles the session's status.
   */
  const finishUpload = useCallback(
    async (
      sessionId: string,
      orthoId: number,
      failedFiles: FailedUpload[],
    ): Promise<void> => {
      const uploadedCount = totalFileCountRef.current - failedFiles.length;
      // A lone failed file shouldn't block ortho creation, but with several the
      // user decides whether to create the ortho from the files that made it.
      const shouldStartOrthoCreation = failedFiles.length <= 1;

      if (uploadedCount > 0 || shouldStartOrthoCreation) {
        await queryClient.invalidateQueries({
          queryKey: getGetAllTasksQueryKey(),
        });
      }
      patchSession(sessionId, { folderPath: null });

      if (shouldStartOrthoCreation) {
        const orthoResponse = await startOrthoCreation({
          ortho_id: orthoId,
        }).catch(() => null);
        if (orthoResponse?.status !== 200) {
          // The workflow shows this as a failed ortho creation.
          onOrthoCreationError(orthoId);
        }
        // Start moves the ortho to processing. Wait for that status and the
        // creation task before the session leaves "uploading", so the workflow
        // never briefly looks stuck between the two.
        await Promise.all([
          orthoResponse?.status === 200
            ? queryClient.invalidateQueries({
                queryKey: getGetOrthosQueryKey(),
              })
            : Promise.resolve(),
          queryClient.invalidateQueries({
            queryKey: getGetAllTasksQueryKey(),
          }),
        ]);
      }

      if (failedFiles.length > 0) {
        patchSession(sessionId, {
          status: "error",
          errorMessage: failedFiles
            .map((file) => `${file.name}: ${file.error ?? "Upload failed"}`)
            .join("; "),
        });
      } else {
        patchSession(sessionId, { status: "success" });
      }
    },
    [onOrthoCreationError, patchSession, queryClient],
  );

  /**
   * Stages `acceptedUploads` in Uppy and drives the S3 transfer, tracking task outcomes
   * as files finish. Shared by `upload` and `resumeUploads`.
   */
  const runUppyUpload = useCallback(
    async (
      sessionId: string,
      acceptedUploads: PreparedUpload[],
      orthoId: number,
      initialCompletedCount = 0,
    ): Promise<void> => {
      beginTransfer(sessionId, acceptedUploads.length, initialCompletedCount);
      replaceUppyFiles(
        uppy,
        acceptedUploads.map((upload) => upload.file),
      );

      const uploadByFile = new Map(
        acceptedUploads.map((upload) => [upload.file, upload]),
      );
      for (const uppyFile of uppy.getFiles()) {
        if (!(uppyFile.data instanceof File)) {
          continue;
        }
        const upload = uploadByFile.get(uppyFile.data);
        if (upload?.uploadUrl == null) {
          continue;
        }
        upload.uppyFileId = uppyFile.id;
        uppy.setFileMeta(uppyFile.id, { uploadUrl: upload.uploadUrl });
        if (upload.taskId != null) {
          uppyFileTaskIdsRef.current.set(uppyFile.id, upload.taskId);
        }
      }

      const result = await withTaskOutcomeFlushing(async () =>
        retryFailedUploads(await uppy.upload()),
      );
      await finishUpload(sessionId, orthoId, result?.failed ?? []);
    },
    [
      beginTransfer,
      finishUpload,
      retryFailedUploads,
      uppy,
      withTaskOutcomeFlushing,
    ],
  );

  /**
   * Uploads zip entries straight from the archive: each is decompressed alone,
   * PUT, and released, so at most `UPLOAD_CONCURRENCY` images are in memory at once.
   * Same bookkeeping as `runUppyUpload`, but without Uppy, which needs every file's
   * bytes up front.
   */
  const runZipUpload = useCallback(
    async (
      sessionId: string,
      acceptedUploads: PreparedZipUpload[],
      orthoId: number,
      initialCompletedCount = 0,
    ): Promise<void> => {
      beginTransfer(sessionId, acceptedUploads.length, initialCompletedCount);

      const failedFiles: FailedUpload[] = [];
      await withTaskOutcomeFlushing(() =>
        runWithConcurrency(
          acceptedUploads,
          UPLOAD_CONCURRENCY,
          async ({ entry, taskId, uploadUrl }) => {
            try {
              await putBlobWithRetry(uploadUrl, await entry.toBlob());
              recordFileOutcome(taskId, "completed");
            } catch (error) {
              failedFiles.push({
                name: entry.name,
                error: error instanceof Error ? error.message : undefined,
              });
              recordFileOutcome(taskId, "failed");
            }
          },
        ),
      );
      await finishUpload(sessionId, orthoId, failedFiles);
    },
    [beginTransfer, finishUpload, recordFileOutcome, withTaskOutcomeFlushing],
  );

  /** Stages files in Uppy for later upload, without hitting the network or S3. */
  const addFiles = useCallback(
    (sessionId: string, files: File[]) => {
      replaceUppyFiles(uppy, files);
      uppyFileTaskIdsRef.current.clear();
      pendingCompletedTaskIdsRef.current.clear();
      pendingFailedTaskIdsRef.current.clear();
      totalFileCountRef.current = 0;
      completedFileCountRef.current = 0;
      activeSessionIdRef.current = sessionId;
      patchSession(sessionId, {
        folderPath: null,
        status: "idle",
        uploadedFileCount: null,
        totalFileCount: null,
        errorMessage: null,
      });
    },
    [patchSession, uppy],
  );

  /**
   * Creates the uploading ortho row unless `existingOrthoId` is already set, then
   * registers `images` as upload tasks for it. Returns the ortho id and the created
   * tasks (each carrying its presigned upload URL). Throws if the backend rejects the
   * ortho or any image.
   */
  const registerImageUploads = useCallback(
    async (
      sessionId: string,
      images: { directoryPath: string; fileHash: string; filename: string }[],
      userFolder: string,
      locationUuid: string,
      flightDateTime: Date,
      siteId: number,
      existingOrthoId?: number | null,
    ): Promise<{ orthoId: number; tasks: TaskResponse[] }> => {
      const formattedFlightDateTime = formatFlightDateTime(flightDateTime);

      let orthoId = existingOrthoId ?? null;
      if (orthoId == null) {
        const orthoResponse = await createUploadingOrtho({
          flight_date: formattedFlightDateTime,
          location_uuid: locationUuid,
          site_id: siteId,
          user_folder: userFolder,
        });
        if (orthoResponse.status !== 200) {
          throw new Error("Failed to create ortho");
        }
        orthoId = orthoResponse.data.id;
      }
      patchSession(sessionId, { orthoId });
      void queryClient.invalidateQueries({
        queryKey: getGetOrthosQueryKey(),
      });

      const response = await createImageUploads({
        files: images.map(({ directoryPath, fileHash, filename }) => ({
          directory_path: directoryPath,
          file_hash: fileHash,
          filename,
          flight_date: formattedFlightDateTime,
          location_uuid: locationUuid,
          site_id: siteId,
        })),
        ortho_id: orthoId,
      });

      if (response.status !== 200) {
        throw new Error("Failed to create image uploads");
      }

      const rejected = response.data.rejected ?? [];

      if (rejected.length > 0) {
        const names = rejected.map((r) => r.file.filename).join(", ");
        throw new Error(
          `${rejected.length} file(s) were rejected: ${names}. Reason: ${rejected[0].reason}`,
        );
      }

      return { orthoId, tasks: response.data.created ?? [] };
    },
    [patchSession, queryClient],
  );

  /**
   * Zip counterpart of the folder branch of `upload`: hashes each entry by streaming it
   * through the hasher, registers them, then uploads each entry as its own image.
   */
  const uploadZipEntries = useCallback(
    async (
      sessionId: string,
      archive: OrthoZipArchive,
      locationUuid: string,
      flightDateTime: Date,
      siteId: number,
      existingOrthoId?: number | null,
    ): Promise<void> => {
      if (archive.entries.length === 0) {
        throw new Error("No JPEG files found in zip");
      }

      const fileHashes = await hashOrthoZipEntries(archive.entries);
      const { orthoId, tasks } = await registerImageUploads(
        sessionId,
        archive.entries.map(({ directoryPath, name }, index) => ({
          directoryPath,
          fileHash: fileHashes[index],
          filename: name,
        })),
        archive.folderName,
        locationUuid,
        flightDateTime,
        siteId,
        existingOrthoId,
      );

      const acceptedUploads = toPreparedZipUploads(
        archive.entries,
        fileHashes,
        mapTasksByHash(tasks),
      );
      if (acceptedUploads.length === 0) {
        throw new Error("No upload URLs were returned");
      }

      await runZipUpload(sessionId, acceptedUploads, orthoId);
    },
    [registerImageUploads, runZipUpload],
  );

  /**
   * Registers `files` as upload tasks for an ortho, then uploads them. A single zip is
   * uploaded as the images inside it, not as one file.
   * Creates the uploading ortho row unless `existingOrthoId` is already set.
   * Throws if the backend rejects the ortho or any files.
   */
  const upload = useCallback(
    async (
      sessionId: string,
      files: File[],
      locationUuid: string,
      flightDateTime: Date,
      siteId: number,
      existingOrthoId?: number | null,
    ): Promise<void> => {
      const zipFile =
        files.length === 1 && isZipFile(files[0]) ? files[0] : null;
      const userFolder =
        files.length > 0
          ? (getFileDirectoryPath(files[0]).split("/").filter(Boolean)[0] ?? "")
          : "";

      patchSession(sessionId, {
        folderPath: userFolder !== "" ? userFolder : null,
        status: "hashing",
        uploadedFileCount: 0,
        totalFileCount: null,
        errorMessage: null,
      });

      try {
        if (zipFile != null) {
          const archive = await openOrthoZip(zipFile);
          try {
            patchSession(sessionId, { folderPath: archive.folderName });
            await uploadZipEntries(
              sessionId,
              archive,
              locationUuid,
              flightDateTime,
              siteId,
              existingOrthoId,
            );
          } finally {
            await archive.close();
          }
          return;
        }

        if (userFolder === "") {
          throw new Error("Select a folder before uploading");
        }

        const fileHashes = await hashFilesSha256(files);

        const uploads: PreparedUpload[] = files.map((file, index) => ({
          file,
          directoryPath: getFileDirectoryPath(file),
          fileHash: fileHashes[index],
        }));

        const { orthoId, tasks } = await registerImageUploads(
          sessionId,
          uploads.map(({ directoryPath, fileHash, file }) => ({
            directoryPath,
            fileHash,
            filename: file.name,
          })),
          userFolder,
          locationUuid,
          flightDateTime,
          siteId,
          existingOrthoId,
        );

        const uploadByHash = new Map(
          uploads.map((upload) => [upload.fileHash, upload]),
        );
        for (const task of tasks) {
          const upload =
            task.file_hash != null
              ? uploadByHash.get(task.file_hash)
              : undefined;
          if (upload == null) {
            continue;
          }
          upload.taskId = task.id;
          upload.uploadUrl = extractUploadUrl(task);
        }

        const acceptedUploads = uploads.filter(
          (upload) => upload.uploadUrl != null,
        );
        if (acceptedUploads.length === 0) {
          throw new Error("No upload URLs were returned");
        }

        await runUppyUpload(sessionId, acceptedUploads, orthoId);
      } catch (error) {
        patchSession(sessionId, {
          status: "error",
          errorMessage:
            error instanceof Error ? error.message : "Failed to upload files",
        });
        throw error;
      }
    },
    [patchSession, registerImageUploads, runUppyUpload, uploadZipEntries],
  );

  /**
   * Resumes previously-registered uploads that never completed. Matches `files` (freshly
   * reselected by the user) against `tasks` by content hash and reuses each match's
   * existing presigned upload URL. Unmatched files are ignored; throws if none match.
   */
  const resumeUploads = useCallback(
    async (
      sessionId: string,
      files: File[],
      tasks: TaskResponse[],
    ): Promise<void> => {
      const alreadyCompletedCount = tasks.filter(
        (task) => task.status === TaskStatus.completed,
      ).length;

      const firstPendingTask = tasks.find(
        (task) => task.status === TaskStatus.pending,
      );
      patchSession(sessionId, {
        folderPath:
          firstPendingTask != null
            ? getTaskTopLevelFolderPath(firstPendingTask.description)
            : null,
        status: "hashing",
        uploadedFileCount: alreadyCompletedCount,
        totalFileCount: null,
        errorMessage: null,
      });

      try {
        const taskWithSummary = tasks.find(
          (task) => task.results?.summary != null,
        );
        const summary = taskWithSummary?.results?.summary;
        if (summary == null || taskWithSummary == null) {
          throw new Error(
            "Could not determine the flight date/location for these uploads",
          );
        }
        const flightDate = getTaskFlightDate(taskWithSummary);
        const orthoId = getTaskOrthoId(taskWithSummary);
        if (flightDate == null || orthoId == null) {
          throw new Error("Could not determine the ortho for these uploads");
        }
        patchSession(sessionId, { orthoId });

        const pendingTaskByHash = mapTasksByHash(
          tasks.filter((task) => task.status === TaskStatus.pending),
        );

        if (files.length === 1 && isZipFile(files[0])) {
          const archive = await openOrthoZip(files[0]);
          try {
            const zipHashes = await hashOrthoZipEntries(archive.entries);
            const acceptedZipUploads = toPreparedZipUploads(
              archive.entries,
              zipHashes,
              pendingTaskByHash,
            );
            if (acceptedZipUploads.length === 0) {
              throw new Error(
                "None of the images in the zip matched the pending uploads for this folder",
              );
            }
            await runZipUpload(
              sessionId,
              acceptedZipUploads,
              orthoId,
              alreadyCompletedCount,
            );
          } finally {
            await archive.close();
          }
          return;
        }

        const fileHashes = await hashFilesSha256(files);

        const acceptedUploads: PreparedUpload[] = [];
        files.forEach((file, index) => {
          const task = pendingTaskByHash.get(fileHashes[index]);
          const uploadUrl = task != null ? extractUploadUrl(task) : undefined;
          if (task == null || uploadUrl == null) {
            return;
          }
          acceptedUploads.push({
            file,
            directoryPath: getFileDirectoryPath(file),
            fileHash: fileHashes[index],
            taskId: task.id,
            uploadUrl,
          });
        });

        if (acceptedUploads.length === 0) {
          throw new Error(
            "None of the selected files matched the pending uploads for this folder",
          );
        }

        await runUppyUpload(
          sessionId,
          acceptedUploads,
          orthoId,
          alreadyCompletedCount,
        );
      } catch (error) {
        patchSession(sessionId, {
          status: "error",
          errorMessage:
            error instanceof Error ? error.message : "Failed to resume uploads",
        });
        throw error;
      }
    },
    [patchSession, runUppyUpload, runZipUpload],
  );

  return {
    uppy,
    addFiles,
    upload,
    resumeUploads,
  };
};
